import React, { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { PlusIcon } from "@heroicons/react/24/outline";
import TrackingAPI from 'api/TrackingAPI';
import styled from 'styled-components';

import magic_stick_icon from '../../../assets/magic_stick_icon.svg';
import CommonHelper from '../../../helpers/CommonHelper';

const ScenarioGapCard = (props) => {
  const { item, onCreateScenario } = props;

  const containerRef = useRef(null);
  const videoRef = useRef();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  const handleCreateScenario = () => {
    TrackingAPI.trackEvent('custom_scenario_gap_card_clicked');
    onCreateScenario();
  };


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (isMobile) {
            setIsVisible(entry.isIntersecting);
            setShouldLoadVideo(true);
          }
          if (entry.isIntersecting && !isMobile) {
            setShouldLoadVideo(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (isVisible) {
        videoRef.current.play().catch(() => { });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isVisible]);

  return (
    <Card
      className="overflow-hidden shadow-lg"
      ref={containerRef}
      onMouseEnter={() => {
        if (item?.videoCoverUrl && !isMobile) {
          setIsVisible(true);
        }
      }}
      onMouseLeave={() => {
        if (item?.videoCoverUrl && !isMobile) {
          setIsVisible(false);
        }
      }}
    >
      <Background image={item.coverUrl} />
      {item?.videoCoverUrl && shouldLoadVideo && (
        <CardVideo
          visible={isVisible}
          ref={videoRef}
          muted
          playsInline
          preLoad={isMobile ? "none" : "auto"}
          preload={isMobile ? "none" : "auto"}
          onEnded={() => setIsVisible(false)}
          loading="lazy"
        >
          <source src={item?.videoCoverUrl} type="video/mp4" />
        </CardVideo>
      )}
      <Overlay />
      <Content>
        <TopContent>
          <WandIcon src={magic_stick_icon} />
          <Title>Create your<br /> Own Scenario</Title>
        </TopContent>
        <PremiumButton onClick={handleCreateScenario}>
          <PlusIcon width={34} height={34} />
          Create scenario
        </PremiumButton>
      </Content>
    </Card>
  );
};

export default ScenarioGapCard;

const Card = styled.div`
  color: white;
  height: 100%;
  font-family: 'Unbounded', sans-serif;
  font-optical-sizing: auto;
  font-size: 16px;
  position: relative;
  transition: transform 0.3s;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(29, 29, 29, 1);

  :hover {
    transform: scale3d(1.03, 1.03, 1);
    box-shadow: rgba(175, 61, 169, 0.57);
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${props => CommonHelper.getFilesCdnUrl(props.image)});
  background-size: cover;
  background-position: center;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(62, 12, 51, 0) 0%, rgba(62, 12, 51, 0.5) 50%, #3E0C33 100%);
  pointer-events: none;
`;

const CardVideo = styled.video`
  z-index: 2;
  object-fit: cover;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  opacity: ${props => (props?.visible ? '1' : '0')};
`;

const Content = styled.div`
  position: relative;
  z-index: 3;
  height: 100%;
  padding: 32px 46px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 720px) {
    padding: 24px;
    gap: 16px;
    min-height: 520px;
  }
`;

const TopContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  flex: 1;
  padding-top: 80px;
`;

const WandIcon = styled.img`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: -16px;
  width: 52px;
  height: 52px;
`;

const Title = styled.h2`
  font-family: "Raleway", sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
  margin: 0;
  white-space: nowrap;
`;

const PremiumButton = styled.div`
  cursor: pointer;
  color: white;
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  height: 48px;
  padding-left: 32px;
  padding-right: 32px;
  border-radius: 1000px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  background: linear-gradient(90deg, #9e5eee 0%, #b759ea 32%, #c65de9 66%, #b885f4 100%);

  @media (max-width: 768px) {
    padding-left: 15px;
    padding-right: 15px;
    width: 100%;
  }
`;